import { useEffect, useState } from 'react';
import Slider from 'react-slick';
import JSZip from 'jszip';
import { useTranslation } from 'react-i18next';
import { Icon, MediaPreview } from '@anytag/shared-atoms';
import { cn, downloadFileFromUrl, isVideoThumbnail } from '@anytag/shared-utils';
import { LeftArrow, RightArrow } from './custom-arrows';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

export interface CarouselProps {
  isReel?: boolean;
  sources: readonly string[];
  className?: string;
  showIcon?: boolean;
  currSlideIdx?: number;
  afterChangeHandler?: (currentSlide: number) => void;
  imageDownload?: { allowed: boolean; filename: string };
}

const SETTINGS = {
  speed: 500,
  infinite: true,
  slidesToShow: 1,
  slidesToScroll: 1,
  adaptiveHeight: true,
  prevArrow: <LeftArrow to="prev" />,
  nextArrow: <RightArrow to="next" />
};

export const Carousel = ({
  isReel,
  showIcon,
  className,
  sources = [],
  currSlideIdx,
  afterChangeHandler,
  imageDownload
}: CarouselProps) => {
  const { t } = useTranslation();
  const [sliderMethods, setSliderMethods] = useState<Slider | null>(null);
  const multipleImages = sources.length > 1;
  const multipleImagesOrPlayableMedia = sources.length > 0; // MediaPreview component can't play a video, so we need to use Slider

  useEffect(() => {
    if (sliderMethods && currSlideIdx !== undefined) {
      sliderMethods?.slickGoTo(currSlideIdx);
    }
  }, [currSlideIdx]);

  const downloadAllMedia = async (sources: readonly string[]) => {
    const zip = new JSZip();

    try {
      const fetchPromises = sources.map(async (src) => {
        const response = await fetch(src, {
          method: 'GET'
        });
        const blob = await response.blob();
        const fileName = src.split('/').pop() || 'file';
        return { fileName, blob };
      });

      const files = await Promise.all(fetchPromises);

      files.forEach(({ fileName, blob }, index) => {
        zip.file(`${index + 1}-${fileName}`, blob);
      });
      // Generate and download zip
      const content = await zip.generateAsync({ type: 'blob' });
      downloadFileFromUrl(window.URL.createObjectURL(content), imageDownload?.filename || 'media.zip');
    } catch (error) {
      console.error('Failed to download files:', error);
    }
  };

  return multipleImagesOrPlayableMedia ? (
    <Slider
      afterChange={afterChangeHandler}
      className={cn(styles.slider, className)}
      ref={(slider) => setSliderMethods(slider)}
      arrows={multipleImages}
      dots={multipleImages}
      {...SETTINGS}
    >
      {sources.map((source) => {
        const isVideo = isVideoThumbnail(source);

        return (
          <div key={source} className={styles.wrapper}>
            {imageDownload?.allowed && (
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent triggering parent onClick
                  if (sources?.length) {
                    downloadAllMedia(sources);
                  } else if (source) {
                    downloadAllMedia([source]);
                  }
                }}
                className={styles.downloadButton}
              >
                <Icon icon="download" size="12px" variant="white" />
                {t('Media Download')}
              </button>
            )}
            {isVideo ? (
              <div className="relative size-full">
                <video
                  src={source}
                  loop={true}
                  muted={true}
                  autoPlay={true}
                  playsInline={true}
                  controls={true}
                  className="sliderImage size-full"
                />
                {showIcon && (
                  <Icon
                    size="16px"
                    variant="white"
                    icon={isReel ? 'clapper' : 'play'}
                    className="absolute top-1 right-1 z-1"
                  />
                )}
              </div>
            ) : (
              //  image type is a default for now, change it if we'll have some other types
              <MediaPreview
                src={source}
                isReel={isReel}
                className="sliderImage size-full"
                showIcon={sources.length > 1 ? showIcon : isReel}
              />
            )}
          </div>
        );
      })}
    </Slider>
  ) : (
    <MediaPreview src={null} isReel={isReel} showIcon={false} className={cn('size-full')} />
  );
};

const styles = {
  slider: cn(
    'h-(--image-thumbnail-height) w-(--image-thumbnail-width) flex-none',
    '[&_.slick-list]:h-full! [&_.slick-slide]:h-full [&_.slick-track]:h-full',
    '[&_div]:size-full [&_div]:rounded-lg [&_div]:outline-none!',
    '[&_.slick-arrow]:z-100! [&_.slick-arrow.slick-next]:right-0! [&_.slick-arrow.slick-prev]:left-0!',
    '[&_.slick-arrow::before]:bg-white! [&_.slick-arrow::before]:text-3xl! [&_.slick-arrow::before]:opacity-80! [&_.slick-arrow::before]:content-[""]! [&_.slick-arrow::before]:text-shadow-[0_0_10px_rgba(0,0,0,0.4)]!',
    '[&_.slick-dots]:right-0! [&_.slick-dots]:bottom-4! [&_.slick-dots]:left-0! [&_.slick-dots]:m-auto! [&_.slick-dots]:w-fit! [&_.slick-dots]:max-w-50! [&_.slick-dots]:rounded-lg! [&_.slick-dots]:bg-white! [&_.slick-dots]:px-0.5! [&_.slick-dots]:py-0!',
    '[&_.slick-dots_li]:m-0! [&_.slick-dots_li]:size-3.5!',
    '[&_.slick-dots_li_button:before]:m-0! [&_.slick-dots_li_button:before]:w-3.5! [&_.slick-dots_li_button:before]:text-xs! [&_.slick-dots_li_button:before]:leading-3.25! [&_.slick-dots_li_button:before]:text-blue-70!'
  ),
  wrapper: cn(
    'relative flex! items-center justify-center bg-black',
    '[&>div:first-of-type]:size-full',
    '[&>div:first-of-type_video]:size-full [&>div:first-of-type_video]:object-contain',
    '[&>div:first-of-img]:size-full [&>div:first-of-type_img]:object-contain'
  ),
  downloadButton: cn(
    'absolute top-3 left-2 z-1 flex cursor-pointer items-center gap-1.5 rounded-[20px] bg-black-90/50 px-2 py-1 text-sm font-semibold text-white hover:bg-black'
  )
};
